import { Box, Radio, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

/** COMPONENT */
import TableCustom from "../../../component/table/tableCustom";
import TableRowCommon from "../../../component/table/TableRowCommon";

/** UTILS */
import { DepositRefundLease } from "../../../api/deposit-refund";
import { colors } from "../../../constants/colors";
import { leaseStatusColor } from "../../../constants/depositRefund";
import { DASH, amountToView, dateOnlyToView } from "../depositRefund.utils";

type Props = {
  leases: DepositRefundLease[];
  /** leaseAgreementId ที่เลือกอยู่ — null = ยังไม่เลือก */
  selectedId: number | null;
  onSelect: (leaseAgreementId: number) => void;
  disabled?: boolean;
  textEmptyData?: string;
};

/** ความกว้างคอลัมน์ radio — TableRowCommon บังคับ width 160 ให้ cell แบบ COMPONENT ถ้าไม่ส่งมา */
const SELECT_COL_WIDTH = 56;

/** ความกว้างคอลัมน์ "เงินมัดจำ" — จองไว้ให้กว้างกว่าข้อความหัวคอลัมน์ ไม่งั้นคอลัมน์จะพอดีตัวจนดูอึดอัด */
const AMOUNT_COL_WIDTH = 150;

/**
 * tableCustom ตั้ง `padding='none'` ให้ head cell ทุกช่องเสมอ (ไม่ว่าจะส่ง disablePadding มาหรือไม่)
 * หัวตารางจึงไม่มีระยะขอบเลย ต่างจาก body cell ที่ได้ 2px 15px จาก .sm-data-table ใน dataTable.css
 * ผลคือหัวคอลัมน์เยื้องจากค่าข้างล่าง และคอลัมน์สุดท้ายชิดขอบตาราง (ยิ่งชนกับ scrollbar
 * แนวตั้งที่มาจาก tableMaxHeight เมื่อมีหลายแถว)
 *
 * แก้เฉพาะตารางนี้ ไม่แตะ tableCustom ที่อีก ~40 หน้าใช้ร่วม
 */
const TableWrap = styled(Box)({
  // ให้หัวตารางมีระยะขอบเท่ากับ body cell หัวคอลัมน์จะได้ตรงกับค่าข้างล่าง
  "& .MuiTableCell-head": { paddingLeft: 15, paddingRight: 15 },
  // คอลัมน์แรกเป็น radio กว้าง 56px ใส่ padding แล้วจะเบียดจนคอลัมน์บวม
  "& .MuiTableCell-head:first-of-type": { paddingLeft: 0, paddingRight: 0 },
  // คอลัมน์สุดท้ายชิดขอบตาราง เว้นขวาให้มากกว่าช่องอื่นทั้งหัวตารางและแถวข้อมูล
  // (`:last-of-type` ทำให้ specificity สูงกว่า 2 กฎบน จึงชนะทั้ง head และ body)
  "& .MuiTableCell-root:last-of-type": { paddingRight: 28 },
});

/**
 * ตาราง "สัญญาเช่าที่มีเงินมัดจำ" ใน modal ส่งคำร้อง — เลือกได้ทีละสัญญาเท่านั้น
 *
 * ใช้ Radio ใน cell แบบ COMPONENT ไม่ใช้ option 'CHECKBOX' ของ TableRowCommon
 * เพราะตัวนั้นผูกกับ InputCheckbox ซึ่งเป็น multi-select และ headCell ที่ label เป็น
 * 'CHECKBOX' จะไปเข้าสาขา dropdown เลือกทั้งหมดใน tableCustom.tsx แล้วพังที่ headCell.values.map
 *
 * ไม่ใช้ RadioGroup เพราะแถวถูกสร้างโดย TableRowCommon ครอบ tbody ไม่ได้ตามกฎ HTML
 * จึงคุม checked/onChange เองต่อแถว แต่ยังใส่ name เดียวกันทุกตัวเพื่อ a11y
 */
export default function DepositContractTable({
  leases,
  selectedId,
  onSelect,
  disabled,
  textEmptyData,
}: Props) {
  const headCells = [
    { id: "select", disablePadding: false, label: "", align: "center", width: SELECT_COL_WIDTH },
    { id: "leaseAgreementNo", disablePadding: false, label: "เลขที่สัญญา", align: "left" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "left" },
    { id: "stallName", disablePadding: false, label: "แผงค้า", align: "left" },
    { id: "stallNumber", disablePadding: false, label: "เลขแผงค้า", align: "center" },
    { id: "dateEnd", disablePadding: false, label: "วันที่หมดสัญญา", align: "center" },
    { id: "leaseStatus", disablePadding: false, label: "สถานะสัญญา", align: "center" },
    {
      id: "depositAmount",
      disablePadding: false,
      label: "เงินมัดจำ",
      align: "right",
      width: AMOUNT_COL_WIDTH,
    },
  ];

  const renderData = (objData: DepositRefundLease, no: number) => {
    const isSelected = objData.leaseAgreementId === selectedId;
    // can_request = false -> มีคำร้องค้างอยู่แล้ว เลือกไม่ได้ ต้องบอกเลขที่คำร้องที่ยึดสิทธิไว้
    const rowDisabled = !!disabled || !objData.can_request;

    const objRenderData = {
      key: `lease-${objData.leaseAgreementId}-${no}`,
      id: `lease-${objData.leaseAgreementId}`,
      obj: objData,
      // ได้ class MuiTableRow-root-active ไฮไลต์แถวที่เลือกฟรี ไม่ต้องเขียน style เพิ่ม
      rowSelect: isSelected,
      // กดที่แถวก็เลือกได้ ไม่ต้องเล็งปุ่ม radio (cell แบบ COMPONENT ไม่รับ onClick นี้)
      onClick: () => {
        if (!rowDisabled) onSelect(objData.leaseAgreementId);
      },
      columns: [
        {
          option: "COMPONENT",
          align: "center",
          width: SELECT_COL_WIDTH,
          // blocked_reason เป็นข้อความไทยพร้อมใช้จาก backend และใช้ helper ตัวเดียวกับ
          // ข้อความ 400 ของ POST -> ถ้อยคำตรงกันทุกทาง ไม่ต้องประกอบเอง
          component: (
            <Tooltip title={objData.can_request ? "" : objData.blocked_reason || ""}>
              <span>
                <Radio
                  name="deposit-refund-lease"
                  value={String(objData.leaseAgreementId)}
                  checked={isSelected}
                  disabled={rowDisabled}
                  onChange={() => onSelect(objData.leaseAgreementId)}
                  size="small"
                />
              </span>
            </Tooltip>
          ),
        },
        {
          option: "TEXT",
          align: "left",
          label: objData.lease_agreement_no || DASH,
          style: rowDisabled ? { color: colors.disabledGray } : undefined,
        },
        // สัญญาที่ยกเลิก/โอนสิทธิแล้ว backend อาจหาแผง/โซนไม่เจอ -> null ตาม spec ข้อ 3.5
        { option: "TEXT", align: "left", label: objData.zone_name || DASH },
        { option: "TEXT", align: "left", label: objData.stall_name || DASH },
        { option: "TEXT", align: "center", label: objData.stall_number || DASH },
        // DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok
        { option: "TEXT", align: "center", label: dateOnlyToView(objData.date_end) },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Typography sx={{ fontSize: 16, color: leaseStatusColor(objData.lease_status) }}>
              {/* backend คืนคำไทยมาให้แล้ว ไม่ต้อง map เอง */}
              {objData.lease_status_name || objData.lease_status || DASH}
            </Typography>
          ),
        },
        {
          option: "TEXT",
          align: "right",
          // deposit_amount เป็น number ตรงๆ (ต่างจาก request_amount ที่เป็น string)
          label: `${amountToView(objData.deposit_amount)} บาท`,
        },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <TableWrap>
      <TableCustom
        headCells={headCells}
        customScroll
        hidePagination
        page={1}
        // ห้ามเป็น 0 เด็ดขาด — Math.ceil(count / 0) = Infinity แล้ว renderPage วนไม่จบ แท็บค้าง
        pageLimit={leases.length || 1}
        sortType="ASC"
        sortBy=""
        rowCount={leases.length}
        rowsData={leases.map((lease, index) => renderData(lease, index))}
        onSort={() => {}}
        setPage={() => {}}
        setPageLimit={() => {}}
        textEmptyData={textEmptyData}
        tableMinWidth={1000}
        // stickyHeader ต้องใช้คู่กับ tableMaxHeight ไม่งั้นไม่มีผล (MUI ต้องการ container สูงจำกัด)
        // และถ้าไม่ส่ง tableMaxHeight ตารางจะได้ minHeight calc(100% - 65px) ซึ่งระเบิดใน modal
        tableMaxHeight={320}
        stickyHeader
      />
    </TableWrap>
  );
}
